// Asana → Work Assignments sync (Workforce Execution Phase 4). Lives inside
// the Asana card: direction (default OFF), watched projects, the assignment
// type inbound tasks become, and the People mapping tab — external users are
// matched by verified work email or mapped explicitly here, never guessed.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { Briefcase, Loader2, RefreshCcw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import {
  getAsanaAssignProfile, listAsanaIdentities, mapAsanaIdentity,
  reconcileAsanaAssignments, updateAsanaAssignProfile,
  type AsanaAssignProfile, type AsanaIdentityLink,
} from '@/services/developerService';
import { listTypes, type AssignmentType } from '@/services/workAssignmentService';
import { getAllEmployees } from '@/lib/hrApi';
import { errMsg, inputCls } from './common';

const ID_BADGE: Record<AsanaIdentityLink['status'], string> = {
  active: 'bg-success-soft text-success-strong border-transparent',
  unmapped: 'bg-warning-soft text-warning-strong border-transparent',
  ambiguous: 'bg-warning-soft text-warning-strong border-transparent',
  orphaned: 'bg-muted text-muted-foreground border-border',
};

export const AsanaAssignmentsSection: React.FC = () => {
  const { t } = useTranslation('admin');
  const [profile, setProfile] = useState<AsanaAssignProfile | null>(null);
  const [types, setTypes] = useState<AssignmentType[]>([]);
  const [identities, setIdentities] = useState<AsanaIdentityLink[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [direction, setDirection] = useState('off');
  const [typeKey, setTypeKey] = useState('');
  const [gids, setGids] = useState<string[]>([]);
  const [newGid, setNewGid] = useState('');
  const [outboundCompleted, setOutboundCompleted] = useState(false);
  const [pick, setPick] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getAsanaAssignProfile().then((p) => {
      setProfile(p);
      setDirection(p.direction);
      setTypeKey(p.typeKey);
      setGids((p.projects || []).map((x) => x.gid));
      setOutboundCompleted((p.outboundEvents || []).includes('completed'));
    }).catch(() => setProfile(null));
    listAsanaIdentities().then(setIdentities).catch(() => setIdentities([]));
  }, []);

  useEffect(() => {
    load();
    listTypes().then(setTypes).catch(() => setTypes([]));
  }, [load]);

  // Employees only needed once someone must be mapped by hand.
  const needsMapping = useMemo(
    () => identities.filter((i) => i.status === 'unmapped' || i.status === 'ambiguous'),
    [identities],
  );
  useEffect(() => {
    if (needsMapping.length > 0 && employees.length === 0) {
      getAllEmployees().then(setEmployees).catch(() => setEmployees([]));
    }
  }, [needsMapping.length, employees.length]);

  const nameByGid = useMemo(() => {
    const m = new Map<string, string>();
    (profile?.projects || []).forEach((p) => m.set(p.gid, p.name || p.gid));
    return m;
  }, [profile]);

  const save = async () => {
    setBusy(true);
    try {
      await updateAsanaAssignProfile({
        direction,
        projectGids: gids,
        typeKey: typeKey || undefined,
        outboundEvents: direction === 'bidirectional' && outboundCompleted ? ['completed'] : [],
      });
      toast.success(t('developer.asana_wa_saved', { defaultValue: 'Assignment sync settings saved.' }));
      load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_wa_save_failed', { defaultValue: 'Could not save assignment sync settings.' }))));
    } finally {
      setBusy(false);
    }
  };

  const runSync = async () => {
    try {
      await reconcileAsanaAssignments();
      toast.success(t('developer.asana_wa_sync_started', { defaultValue: 'Sync started — new tasks appear as assignments shortly.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_wa_sync_failed', { defaultValue: 'Could not start the sync.' }))));
    }
  };

  const doMap = async (link: AsanaIdentityLink) => {
    const empId = pick[link.id];
    if (!empId) return;
    const emp = employees.find((e) => e.id === empId);
    try {
      await mapAsanaIdentity(link.id, empId, emp?.user_id || undefined);
      toast.success(t('developer.asana_wa_mapped', { defaultValue: 'Mapped — their tasks sync on the next sweep.' }));
      load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_wa_map_failed', { defaultValue: 'Could not map this person.' }))));
    }
  };

  if (!profile) return null;

  return (
    <div className="rounded-lg border border-border p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold">
            {t('developer.asana_wa_title', { defaultValue: 'Work assignments' })}
          </span>
          {direction !== 'off' && (
            <Badge className="bg-success-soft text-success-strong border-transparent capitalize">{direction}</Badge>
          )}
        </div>
        {direction !== 'off' && (
          <Button size="sm" variant="outline" onClick={runSync}>
            <RefreshCcw className="w-3.5 h-3.5 mr-1" />
            {t('developer.asana_wa_sync_now', { defaultValue: 'Sync now' })}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {t('developer.asana_wa_hint', {
          defaultValue: 'Tasks in the watched projects become work assignments for the assigned person — presence and attendance follow their check-ins in KaziPeople, never Asana. Off by default.',
        })}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm flex items-center gap-2">
          {t('developer.asana_wa_direction', { defaultValue: 'Direction' })}
          <select className={`${inputCls} h-8 text-sm w-[170px]`} value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="off">{t('developer.asana_wa_off', { defaultValue: 'Off' })}</option>
            <option value="inbound">{t('developer.asana_wa_inbound', { defaultValue: 'Asana → KaziPeople' })}</option>
            <option value="bidirectional">{t('developer.asana_wa_bidi', { defaultValue: 'Both directions' })}</option>
          </select>
        </label>
        <label className="text-sm flex items-center gap-2">
          {t('developer.asana_wa_type', { defaultValue: 'Assignment type' })}
          <select className={`${inputCls} h-8 text-sm w-[170px]`} value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
            {types.filter((x) => x.active).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            {types.length === 0 && <option value={typeKey}>{typeKey}</option>}
          </select>
        </label>
        {direction === 'bidirectional' && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={outboundCompleted} onChange={(e) => setOutboundCompleted(e.target.checked)} />
            {t('developer.asana_wa_outbound', { defaultValue: 'Check-out completes the Asana task' })}
          </label>
        )}
      </div>

      {direction !== 'off' && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('developer.asana_wa_projects', { defaultValue: 'Watched projects' })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {gids.map((g) => (
              <span key={g} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
                {nameByGid.get(g) || g}
                <button type="button" onClick={() => setGids((cur) => cur.filter((x) => x !== g))} aria-label="remove">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </span>
            ))}
            <input
              className={`${inputCls} h-8 text-xs w-[220px]`}
              placeholder={t('developer.asana_project_ph', { defaultValue: 'Asana project GID (from the project URL)' })}
              value={newGid}
              onChange={(e) => setNewGid(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newGid.trim()) {
                  setGids((cur) => Array.from(new Set([...cur, newGid.trim()])));
                  setNewGid('');
                }
              }}
            />
            <Button size="sm" variant="outline" disabled={!newGid.trim()}
              onClick={() => { setGids((cur) => Array.from(new Set([...cur, newGid.trim()]))); setNewGid(''); }}>
              {t('add', { defaultValue: 'Add' })}
            </Button>
          </div>
        </div>
      )}

      <Button size="sm" onClick={save} disabled={busy}>
        {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
        {t('save', { defaultValue: 'Save' })}
      </Button>

      {identities.length > 0 && (
        <div className="space-y-2 pt-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('developer.asana_wa_people', { defaultValue: 'People mapping' })}
          </p>
          <div className="space-y-1.5">
            {identities.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-foreground">{l.externalName || l.externalUserId}</span>
                {l.externalEmail && <span className="text-xs text-muted-foreground">{l.externalEmail}</span>}
                <Badge className={ID_BADGE[l.status]}>{l.status}</Badge>
                {(l.status === 'unmapped' || l.status === 'ambiguous') && (
                  <>
                    <select
                      className={`${inputCls} h-7 text-xs w-[220px]`}
                      value={pick[l.id] || ''}
                      onChange={(e) => setPick((cur) => ({ ...cur, [l.id]: e.target.value }))}
                    >
                      <option value="">{t('developer.asana_wa_pick_emp', { defaultValue: 'Choose employee…' })}</option>
                      {employees.map((e) => (
                        <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.email}</option>
                      ))}
                    </select>
                    <Button size="sm" variant="outline" disabled={!pick[l.id]} onClick={() => doMap(l)}>
                      {t('developer.asana_wa_map', { defaultValue: 'Map' })}
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
